import { AssetTag } from "../../swagger/caasService-v1";
/** Per-metric display unit shown in legends / in-bar text. */
const METRIC_UNITS = {
    CycleTime: "",
    EnergyUsed: "kWh",
    IdleTime: "%",
    LoadCount: "cycles",
    MaterialMoved: "tonnes"
};
/** Rotating palette for cycle-time donut slices when an activity is unknown. */
const DONUT_FALLBACK_COLORS = ["#0066FF", "#0DB3A0", "#FFA230", "#86BAFF", "#777777", "#B366FF"];
/** Preferred colours for well-known cycle-time activities. */
const DONUT_KNOWN_COLORS = {
    Carry: "#FFA230",
    Charging: "#0DB3A0",
    Dig: "#B366FF",
    Dump: "#86BAFF",
    Idle: "#86BAFF",
    Load: "#777777"
};
/** Maps the segmented asset filter to the API `assetTag` (or `null` for All). */
const assetFilterToTag = (assetFilter) => {
    if (assetFilter === "Haulers")
        return AssetTag.HAULER;
    if (assetFilter === "Loaders")
        return AssetTag.LOADER;
    return null;
};
/** Reads the numeric value for the selected bar metric from a productivity detail. */
const readMetricValue = (detail, metric) => {
    switch (metric) {
        case "EnergyUsed":
            return detail.energyUsed ?? 0;
        case "IdleTime":
            return detail.idleTime ?? 0;
        case "LoadCount":
            return detail.loadCount ?? 0;
        case "MaterialMoved":
            return detail.materialMoved ?? 0;
        default:
            return 0;
    }
};
/**
 * Builds human-readable, unique per-asset tick labels. When the same `assetTag`
 * appears more than once (e.g. two Loaders) the labels are numbered so each bar
 * is distinguishable; unique tags are shown as-is. Falls back to a shortened
 * `assetId`, then a positional label, when a tag is missing.
 */
const buildAssetLabels = (details) => {
    const tagTotals = new Map();
    details.forEach((detail) => {
        if (detail.assetTag) {
            tagTotals.set(detail.assetTag, (tagTotals.get(detail.assetTag) ?? 0) + 1);
        }
    });
    const tagSeen = new Map();
    return details.map((detail, index) => {
        if (detail.assetTag) {
            if ((tagTotals.get(detail.assetTag) ?? 0) > 1) {
                const occurrence = (tagSeen.get(detail.assetTag) ?? 0) + 1;
                tagSeen.set(detail.assetTag, occurrence);
                return `${detail.assetTag} ${occurrence}`;
            }
            return detail.assetTag;
        }
        if (detail.assetId)
            return detail.assetId.slice(0, 8);
        return `Asset ${index + 1}`;
    });
};
const buildBarSeries = (response, metric, assetFilter, dateKey) => {
    const tag = assetFilterToTag(assetFilter);
    const details = (response.productivityDetails ?? []).filter((detail) => tag === null || detail.assetTag === tag);
    return {
        assetFilter,
        data: details.map((detail) => readMetricValue(detail, metric)),
        dateKey,
        metric,
        tickLabels: buildAssetLabels(details),
        type: "bar",
        unit: METRIC_UNITS[metric]
    };
};
/** Rounds to at most two decimals for display. */
const roundDuration = (value) => Math.round(value * 100) / 100;
const buildDonutSeries = (response, assetFilter, dateKey) => {
    const tag = assetFilterToTag(assetFilter);
    const activities = (response.activities ?? []).filter((activity) => tag === null || activity.assetTag === tag);
    // Aggregate durations by activity name so repeated activities collapse into
    // a single slice / table row.
    const totals = new Map();
    activities.forEach((activity) => {
        const label = activity.activity ?? "Unknown";
        const previous = totals.get(label);
        totals.set(label, {
            duration: (previous?.duration ?? 0) + (activity.duration ?? 0),
            unit: activity.unitOfMeasure ?? previous?.unit ?? ""
        });
    });
    const slices = [];
    const activityRows = [];
    let colorIndex = 0;
    totals.forEach((value, label) => {
        const color = DONUT_KNOWN_COLORS[label] ?? DONUT_FALLBACK_COLORS[colorIndex % DONUT_FALLBACK_COLORS.length];
        colorIndex += 1;
        slices.push({ color, label, value: roundDuration(value.duration) });
        activityRows.push({
            label,
            timeLabel: value.unit
                ? `${roundDuration(value.duration)} ${value.unit}`
                : `${roundDuration(value.duration)}`
        });
    });
    return {
        activities: activityRows,
        assetFilter,
        dateKey,
        metric: "CycleTime",
        slices,
        type: "donut"
    };
};
/**
 * Maps a `ProjectEnergyHistogramResponse` (aggregation = productivity) into the
 * chart series consumed by the Energy &amp; Productivity panel. Cycle Time
 * resolves to a donut series built from `activities`; every other metric
 * resolves to a bar series built from `productivityDetails`.
 */
export const mapProductivityResponseToSeries = ({ assetFilter, dateKey, metric, response }) => {
    if (metric === "CycleTime") {
        return buildDonutSeries(response, assetFilter, dateKey);
    }
    return buildBarSeries(response, metric, assetFilter, dateKey);
};
